import { fail } from './errors.js';

/*
 * 가입 계정과 자녀 프로필.
 * - 스스로 가입할 수 있는 역할: 센터장(가입하며 새 센터를 무료체험으로 연다)·치료사(센터 가입 코드 필요)·보호자·학생(만 14세 이상)
 * - 본사 관리자는 스스로 가입할 수 없다(infra/create-admin.ps1)
 * - 만 14세 미만 아동은 개인정보 보호법상 법정대리인 동의가 필요하므로 계정을 직접 만들지 않고,
 *   보호자가 '자녀'로 등록한다(보호자당 4명까지). 아이는 보호자 기기의 '자녀 모드'로 학생 화면을 쓴다.
 * - 만 14세 이상 만 19세 미만은 직접 가입할 수도, 보호자가 자녀로 등록할 수도 있다. 자녀로 등록하려면 본인 동의를 확인한다.
 */

export const SIGNUP_ROLES = ['CENTER_DIRECTOR', 'THERAPIST', 'GUARDIAN', 'STUDENT'] as const;
export type SignupRole = (typeof SIGNUP_ROLES)[number];
export const MAX_CHILDREN = 4;
export const CHILD_AGE_LIMIT = 14; // 이 나이부터 직접 가입할 수 있다
export const CHILD_REGISTER_LIMIT = 19; // 이 나이 미만을 자녀로 등록할 수 있다

const DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const PHONE_RE = /^01[016789]-?\d{3,4}-?\d{4}$/;

export interface UserProfile {
  userId: string;
  role: SignupRole;
  name: string;
  phone?: string;
  birthDate?: string;  // 학생만
  withdrawnAt?: string; // 탈퇴 시각. 이름·연락처는 지운다
  passUntil?: string;  // 학생 월 이용권 만료 시각
  centerId?: string;   // 치료사: 가입 코드로 들어간 센터, 센터장: 가입하며 연 센터
  independent?: boolean; // 센터 없이 활동하는 개인 선생님(선생님 찾기만 쓴다)
  createdAt: string;
}

/** 탈퇴 확인 문구. 실수로 누르는 것을 막는다. */
export const WITHDRAW_CONFIRM = '탈퇴합니다';

/** 탈퇴한 프로필: 식별 정보를 지우고 기록용 최소 값만 남긴다. */
export const withdrawnProfile = (p: UserProfile, now: string): UserProfile =>
  ({ userId: p.userId, role: p.role, name: '탈퇴한 회원', createdAt: p.createdAt, withdrawnAt: now });

export interface ChildProfile {
  id: string;
  guardianId: string;
  name: string;
  birthDate: string;
  /** 센터 이용자와 연결되면 그 센터의 과제를 학생 화면에서 볼 수 있다 */
  link?: { centerId: string; clientId: string };
  teenAgreedAt?: string; // 만 14세 이상: 보호자가 자녀 본인의 동의를 확인한 시각
  createdAt: string;
}

/** 만 나이(한국 시간 기준 오늘). */
export function ageOn(birthDate: string, todayKst: string): number {
  const [by, bm, bd] = birthDate.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = todayKst.split('-').map(Number) as [number, number, number];
  return ty - by - (tm < bm || (tm === bm && td < bd) ? 1 : 0);
}

function validBirthDate(birthDate: string, todayKst: string) {
  if (!DATE_RE.test(birthDate) || Number.isNaN(Date.parse(`${birthDate}T00:00:00Z`))) fail('VALIDATION', '생년월일을 YYYY-MM-DD 형식으로 입력하세요.');
  if (birthDate > todayKst) fail('VALIDATION', '생년월일이 오늘보다 늦습니다.');
}

const cleanName = (name: string) => {
  const n = name.trim().replace(/\s+/g, ' ');
  if (n.length < 1 || n.length > 20) fail('VALIDATION', '이름은 1~20자로 입력하세요.');
  return n;
};

/** 자녀 등록: 만 19세 미만, 보호자당 4명, 같은 이름·생년월일 중복 불가. 만 14세 이상은 본인 동의 확인이 필요하다. */
export function newChildProfile(
  i: { id: string; guardianId: string; name: string; birthDate: string; now: string; todayKst: string; teenAgreed?: boolean },
  existing: readonly ChildProfile[],
): ChildProfile {
  const name = cleanName(i.name);
  validBirthDate(i.birthDate, i.todayKst);
  const age = ageOn(i.birthDate, i.todayKst);
  if (age >= CHILD_REGISTER_LIMIT) fail('VALIDATION', `자녀는 만 ${CHILD_REGISTER_LIMIT}세 미만만 등록할 수 있습니다.`);
  if (age >= CHILD_AGE_LIMIT && !i.teenAgreed) fail('VALIDATION', `만 ${CHILD_AGE_LIMIT}세 이상 자녀는 본인 동의를 확인해 주세요.`);
  if (existing.length >= MAX_CHILDREN) fail('CONFLICT', `자녀는 ${MAX_CHILDREN}명까지 등록할 수 있습니다.`);
  if (existing.some((c) => c.name === name && c.birthDate === i.birthDate)) fail('CONFLICT', '이미 등록한 자녀입니다.');
  return { id: i.id, guardianId: i.guardianId, name, birthDate: i.birthDate, ...(age >= CHILD_AGE_LIMIT ? { teenAgreedAt: i.now } : {}), createdAt: i.now };
}

/** 가입 직후 프로필. 학생은 만 14세 이상만(미만은 보호자가 자녀로 등록). */
export function newUserProfile(i: { userId: string; role: SignupRole; name: string; phone?: string; birthDate?: string; centerId?: string; independent?: boolean; now: string; todayKst: string }): UserProfile {
  if (!SIGNUP_ROLES.includes(i.role)) fail('VALIDATION', '가입할 수 없는 역할입니다.');
  const name = cleanName(i.name);
  const phone = i.phone?.trim() || undefined;
  if (phone && !PHONE_RE.test(phone)) fail('VALIDATION', '휴대폰 번호 형식을 확인하세요(예: 010-1234-5678).');
  let birthDate: string | undefined;
  if (i.role === 'STUDENT') {
    birthDate = i.birthDate ?? fail('VALIDATION', '학생은 생년월일을 입력하세요.');
    validBirthDate(birthDate, i.todayKst);
    if (ageOn(birthDate, i.todayKst) < CHILD_AGE_LIMIT) fail('VALIDATION', `만 ${CHILD_AGE_LIMIT}세 미만은 직접 가입할 수 없습니다. 보호자가 가입한 뒤 자녀로 등록해 주세요.`);
  }
  if (i.role === 'THERAPIST' && !i.centerId && !i.independent) fail('VALIDATION', '선생님은 센터 가입 코드가 필요합니다.');
  if (i.role === 'CENTER_DIRECTOR' && !i.centerId) fail('VALIDATION', '센터 이름을 입력하세요.');
  const withCenter = i.role === 'THERAPIST' || i.role === 'CENTER_DIRECTOR';
  return { userId: i.userId, role: i.role, name, phone, birthDate, centerId: withCenter ? i.centerId : undefined, ...(i.role === 'THERAPIST' && i.independent ? { independent: true } : {}), createdAt: i.now };
}

/** 센터장이 가입하며 여는 센터 이름: 2~40자. */
export function cleanCenterName(name: string): string {
  const n = name.trim().replace(/\s+/g, ' ');
  if (n.length < 2 || n.length > 40) fail('VALIDATION', '센터 이름은 2~40자로 입력하세요.');
  return n;
}

/*
 * 보호자 연결: 센터가 이용자마다 일회용 연결 코드를 만들어 보호자에게 건네고(종이·문자 등),
 * 보호자가 '우리 아이'에서 자녀에게 그 코드를 넣으면 자녀와 센터 이용자가 이어진다.
 * 새 코드를 만들면 이전 코드는 쓸 수 없고, 한 번 쓰면 사라진다.
 */
export const GUARDIAN_INVITE_DAYS = 7;
export const MAX_GUARDIANS = 4; // 이용자 한 명에 연결할 수 있는 보호자 계정 수

export interface GuardianInvite {
  code: string;      // 예: K7PQ-2MXA
  centerId: string;
  clientId: string;
  expiresAt: string;
  by: string;        // 만든 직원
  createdAt: string;
  usedAt?: string;   // 쓴 코드는 지우지 않고 표시만 한다(API 는 DB 삭제 권한이 없다)
}

/** 입력한 코드를 저장 형식으로(대소문자·공백·하이픈 차이 무시). */
export function normalizeInviteCode(code: string): string {
  const raw = code.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return raw.length === 8 ? `${raw.slice(0, 4)}-${raw.slice(4)}` : raw;
}

/**
 * 연결 전 검사. 코드가 맞고 기한 안이어야 하고, 자녀 출생연도가 센터 기록과 같아야 한다(다른 아이에 잘못 붙는 것을 막는다).
 * 코드가 틀린 경우와 기한이 지난 경우는 같은 문구로 답한다.
 */
export function checkGuardianLink(i: {
  child: ChildProfile; guardianId: string; code: string; invite: GuardianInvite | undefined;
  client: { birthYear: number; guardianIds: readonly string[] }; now: string;
}): void {
  if (!i.invite || i.invite.code !== i.code || i.invite.usedAt || i.invite.expiresAt <= i.now) fail('VALIDATION', '연결 코드가 맞지 않거나 기한이 지났습니다. 센터에 새 코드를 요청하세요.');
  if (i.child.link) fail('CONFLICT', '이미 센터와 연결된 자녀입니다.');
  if (i.client.guardianIds.includes(i.guardianId)) fail('CONFLICT', '이 보호자 계정은 이미 이 아이와 연결되어 있습니다.');
  if (i.client.guardianIds.length >= MAX_GUARDIANS) fail('CONFLICT', `한 아이에 보호자 계정은 ${MAX_GUARDIANS}개까지 연결할 수 있습니다.`);
  if (Number(i.child.birthDate.slice(0, 4)) !== i.client.birthYear) fail('VALIDATION', '자녀 출생연도가 센터 기록과 다릅니다. 생년월일을 확인하거나 센터에 문의하세요.');
}
